import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Manufacturing Industry page component
 * Showcases Raidu's AI solutions for the manufacturing industry
 * @returns {JSX.Element} The rendered Manufacturing component
 */
const Manufacturing: React.FC = () => {
  // State for tracking active tab
  const [activeTab, setActiveTab] = useState<string>('challenges');
  
  // State for tracking if component is mounted
  const [isMounted, setIsMounted] = useState<boolean>(false);
  
  // Effect to handle component mount
  useEffect(() => {
    try {
      // Log component mount
      console.log('[Manufacturing] Component mounted');
      setIsMounted(true);
      
      // Cleanup function
      return () => {
        console.log('[Manufacturing] Component unmounted');
      };
    } catch (error) {
      // Log any errors in the effect
      console.error('[Manufacturing] Error in component effect:', error);
    }
  }, []);

  // Manufacturing use cases data
  const useCases = [
    {
      title: 'Predictive Maintenance',
      description: 'AI-powered systems that predict equipment failures before they occur, reducing downtime and maintenance costs.',
      icon: '🔧',
    },
    {
      title: 'Quality Control',
      description: 'Automated inspection systems that detect defects and ensure product quality with greater accuracy than manual inspection.',
      icon: '✓',
    },
    {
      title: 'Supply Chain Optimization',
      description: 'AI solutions that optimize inventory levels, logistics, and supplier management for maximum efficiency.',
      icon: '🔄',
    },
    {
      title: 'Process Optimization',
      description: 'Intelligent systems that analyze production processes and recommend improvements to increase efficiency and reduce waste.',
      icon: '⚙️',
    },
  ];

  // Manufacturing challenges data
  const challenges = [
    {
      title: 'Data Security & IP Protection',
      description: 'Protecting proprietary manufacturing processes, designs, and intellectual property while leveraging AI capabilities.',
    },
    {
      title: 'Legacy System Integration',
      description: 'Integrating AI solutions with existing manufacturing equipment and systems, some of which may be decades old.',
    },
    {
      title: 'Skill Gaps',
      description: 'Addressing the skills gap between traditional manufacturing expertise and the digital skills needed for AI implementation.',
    },
    {
      title: 'ROI Justification',
      description: 'Demonstrating clear return on investment for AI implementations in manufacturing environments.',
    },
  ];

  // Manufacturing solutions data
  const solutions = [
    {
      title: 'Private RAG Technology',
      description: 'Our private retrieval-augmented generation ensures manufacturing data never leaves your secure environment while enabling powerful AI insights.',
    },
    {
      title: 'Multi-LLM Provider Support',
      description: 'Access the best AI models for specific manufacturing tasks while maintaining complete data control and privacy.',
    },
    {
      title: 'Data Masking & Anonymization',
      description: 'Automatically detect and mask sensitive information to protect intellectual property and trade secrets.',
    },
    {
      title: 'Cloud-Agnostic Deployment',
      description: 'Deploy Raidu in your existing cloud environment or on-premises infrastructure for maximum security and compliance.',
    },
  ];

  try {
    return (
      <div className="bg-gradient-to-b from-indigo-50 to-white">
        {/* Hero section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-indigo-500 opacity-10 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                AI Solutions for Manufacturing
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Secure, efficient, and innovative AI implementations that optimize manufacturing processes while protecting intellectual property.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/company/contact"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  onClick={() => {
                    try {
                      console.log('[Manufacturing] User clicked contact us CTA');
                    } catch (error) {
                      console.error('[Manufacturing] Error handling contact CTA click:', error);
                    }
                  }}
                >
                  Schedule a Consultation
                </Link>
                <a
                  href="#use-cases"
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  onClick={(e) => {
                    try {
                      e.preventDefault();
                      document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth' });
                      console.log('[Manufacturing] User clicked explore use cases');
                    } catch (error) {
                      console.error('[Manufacturing] Error handling explore use cases click:', error);
                    }
                  }}
                >
                  Explore Use Cases
                </a>
              </div>
            </div>
          </div>
          
          {/* Decorative wave pattern */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
              <path fill="#ffffff" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
            </svg>
          </div>
        </div>
        
        {/* Tabs section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-8">
              {['challenges', 'solutions', 'use-cases'].map((tab) => (
                <button
                  key={tab}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-md
                    ${activeTab === tab 
                      ? 'border-indigo-500 text-indigo-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                  onClick={() => {
                    try {
                      setActiveTab(tab);
                      console.log(`[Manufacturing] User switched to ${tab} tab`);
                    } catch (error) {
                      console.error(`[Manufacturing] Error switching to ${tab} tab:`, error);
                    }
                  }}
                >
                  {tab === 'challenges' && 'Industry Challenges'}
                  {tab === 'solutions' && 'Our Solutions'}
                  {tab === 'use-cases' && 'Use Cases'}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Tab content */}
          <div className="mt-8">
            {/* Challenges tab */}
            {activeTab === 'challenges' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {challenges.map((challenge, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{challenge.title}</h3>
                    <p className="text-gray-600">{challenge.description}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Solutions tab */}
            {activeTab === 'solutions' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {solutions.map((solution, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{solution.title}</h3>
                    <p className="text-gray-600">{solution.description}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Use Cases tab */}
            {activeTab === 'use-cases' && (
              <div id="use-cases" className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {useCases.map((useCase, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <div className="text-3xl mb-4">{useCase.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{useCase.title}</h3>
                    <p className="text-gray-600">{useCase.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        

        
        {/* CTA section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to transform manufacturing with AI?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Contact us to learn how Raidu can help your manufacturing organization leverage AI while maintaining security and efficiency.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/company/contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
              onClick={() => {
                try {
                  console.log('[Manufacturing] User clicked contact us CTA');
                } catch (error) {
                  console.error('[Manufacturing] Error handling contact CTA click:', error);
                }
              }}
            >
              Contact Us
            </Link>
            <Link
              to="/industries"
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              onClick={() => {
                try {
                  console.log('[Manufacturing] User clicked back to industries');
                } catch (error) {
                  console.error('[Manufacturing] Error handling back to industries click:', error);
                }
              }}
            >
              View All Industries
            </Link>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('[Manufacturing] Error rendering component:', error);
    return (
      <div className="py-16 px-4 text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
        <p className="text-gray-700">An error occurred while rendering the Manufacturing page.</p>
      </div>
    );
  }
};

export default Manufacturing;
